import {
  MarkerClusterer,
  Marker as MarkerType,
} from '@googlemaps/markerclusterer';
import { useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import markerPlain from '~/assets/images/marker-plain.svg';
import { useView } from '~/hooks';
import { useAppDispatch, useAppSelector } from '~/state';
import {
  clearAllSelected,
  selectSelectedClinic,
  selectSelectedPractitioner,
  setSelectedClinic,
  setSelectedPractitioner,
} from '~/state/activeSelection';
import { selectLocation } from '~/state/location';
import {
  selectLocationManuallySelected,
  selectMapBounds,
  selectMapInteractionActive,
  setMapBounds,
} from '~/state/mapData';
import {
  boundsAreNotEqual,
  LoadingStatus,
  SearchResultGroupByLocation,
  Views,
} from '~/utilities';
import { Marker } from './Marker';

interface MarkersListProps {
  mapRestriction: google.maps.MapRestriction;
  results: SearchResultGroupByLocation[];
  loading?: LoadingStatus;
}

export const MarkersList = ({
  mapRestriction,
  results,
  loading: searchLoading = 'idle',
}: MarkersListProps) => {
  const dispatch = useAppDispatch();
  const map = useMap();
  const { handleViewChange, isMobile } = useView();
  const selectedClinic = useAppSelector(selectSelectedClinic);
  const selectedPractitioner = useAppSelector(selectSelectedPractitioner);
  const mapInteractionActive = useAppSelector(selectMapInteractionActive);
  const mapBounds = useAppSelector(selectMapBounds);
  const locationManuallySelected = useAppSelector(
    selectLocationManuallySelected
  );
  const location = useAppSelector(selectLocation);
  const [markers, setMarkers] = useState<{
    [key: string]: MarkerType;
  }>({});
  const clustererRef = useRef<MarkerClusterer | null>(null);

  // Track a bunch of values as refs that we need to read
  // for the useEffect that pans / zooms the map, without
  // them adding them as dependencies
  const locationManuallySelectedRef = useRef(locationManuallySelected);
  const searchLoadingRef = useRef(searchLoading);
  const prevSearchLoading = useRef(searchLoading);
  const resultsRef = useRef(results);
  const mapInteractionActiveRef = useRef(mapInteractionActive);
  const mapBoundsRef = useRef(mapBounds);
  const mapRef = useRef(map);

  // We cannot use results in a dependency array - arrays and objects compared by ref,
  // which often change even though the values are the same.
  // Using results.length doesn't always work, because different searches might have
  // different results, but the same length (usually the max page size of 50)
  const firstResult = results.at(0)?.clinicLocationGuid || '';

  // Update all refs
  useEffect(() => {
    locationManuallySelectedRef.current = locationManuallySelected;
    prevSearchLoading.current = searchLoadingRef.current;
    searchLoadingRef.current = searchLoading;
    resultsRef.current = results;
    mapInteractionActiveRef.current = mapInteractionActive;
    mapRef.current = map;
  }, [
    locationManuallySelected,
    searchLoading,
    results,
    mapInteractionActive,
    map,
  ]);

  // Initialize clusterer once when map is available
  useEffect(() => {
    if (!map) return;

    clustererRef.current = new MarkerClusterer({
      map,
      renderer: {
        render: ({ count, position }) => {
          const clusterContent = document.createElement('div');
          clusterContent.innerHTML = `
          <div class="cluster">
            <img src="${markerPlain}" height={22} width={29} alt="cluster marker" class="hover-scale" />
            <div class="cluster-count">${count}</div>
          </div>
          `;
          clusterContent.style.position = 'relative';

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterContent,
          });
        },
      },
      // MPL-1442: This is a hack to prevent the map restriction from being applied when a cluster is clicked
      // and it causes the map to zoom in to the map center instead of the cluster bounds.
      // When we remove the restriction, this can be removed.
      // https://github.com/googlemaps/js-markerclusterer/issues/601
      onClusterClick: (_, cluster, map) => {
        const bounds = cluster.bounds;
        // Temporarily remove map restriction
        map.setOptions({
          restriction: null,
        });
        // Zoom to cluster bounds
        if (bounds) {
          map.fitBounds(bounds);

          // Re-apply map restriction
          map.setOptions({
            restriction: mapRestriction,
          });
        }
      },
    });

    // Cleanup
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    };
  }, [map, mapRestriction]);

  // Update markers when they change
  useEffect(() => {
    if (!clustererRef.current) return;
    clustererRef.current.clearMarkers();
    clustererRef.current.addMarkers(Object.values(markers));
  }, [markers]);

  // Fit and pan the map to the search results after search completes
  useEffect(() => {
    if (
      prevSearchLoading.current === 'succeeded' &&
      searchLoading === 'succeeded' &&
      !locationManuallySelectedRef.current
    ) {
      return;
    }

    if (searchLoading !== 'succeeded') {
      return;
    }

    // We don't want to fit / pan though if the results are empty,
    // the user manually interacted with the map, or the location wasn't manually selected
    if (
      !resultsRef.current.length ||
      mapInteractionActiveRef.current ||
      !locationManuallySelectedRef.current
    )
      return;

    const bounds = new google.maps.LatLngBounds();
    resultsRef.current.forEach((result) =>
      bounds.extend({
        lat: result.locationCoordinates.latitude,
        lng: result.locationCoordinates.longitude,
      })
    );
    mapRef.current?.fitBounds(bounds);
    mapRef.current?.panToBounds(bounds);

    // Gets the actual bounds from the map after fitting the results.
    // The calculated bounds may have "tighter" bounds then the map
    // if we are really zoomed in
    const actualBounds = mapRef.current?.getBounds();
    if (actualBounds) {
      const boundsLiteral: google.maps.LatLngBoundsLiteral = {
        east: actualBounds.getNorthEast().lng(),
        north: actualBounds.getNorthEast().lat(),
        south: actualBounds.getSouthWest().lat(),
        west: actualBounds.getSouthWest().lng(),
      };
      if (boundsAreNotEqual(boundsLiteral, mapBoundsRef.current)) {
        dispatch(setMapBounds(boundsLiteral));
      }
    }
  }, [dispatch, searchLoading, firstResult]);

  // Pan to searched location even when no results
  useEffect(() => {
    if (
      !location?.coordinates ||
      results.length ||
      mapInteractionActive ||
      !locationManuallySelectedRef.current
    )
      return;

    map?.panTo({
      lat: location.coordinates.lat,
      lng: location.coordinates.lng,
    });
  }, [map, location, results.length, mapInteractionActive]);

  // pans the map to the selected clinic or practitioner
  useEffect(() => {
    // Don't pan if we're on mobile (since we'll switch to list view)
    if (isMobile) {
      return;
    }

    if (selectedClinic) {
      map?.panTo({
        lat: selectedClinic.locationCoordinates.latitude,
        lng: selectedClinic.locationCoordinates.longitude,
      });
    }
    if (selectedPractitioner) {
      map?.panTo({
        lat: selectedPractitioner.locationCoordinates.latitude,
        lng: selectedPractitioner.locationCoordinates.longitude,
      });
    }
  }, [map, selectedClinic, selectedPractitioner, isMobile]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map & adds/removes markers
  const setMarkerRef = useCallback((marker: MarkerType | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleMarkerClick = useCallback(
    (isMultiple: boolean, result: SearchResultGroupByLocation) => {
      dispatch(clearAllSelected());

      if (isMultiple) {
        dispatch(setSelectedClinic(result));
        // On mobile, switch to List view when multi practitioner clinic marker is clicked
        if (isMobile) {
          handleViewChange(Views.List);
        }
      } else {
        dispatch(setSelectedPractitioner(result.practitioners[0]));
      }
    },
    [dispatch, handleViewChange, isMobile]
  );

  return results.map((result) => (
    <Marker
      key={result.clinicLocationGuid}
      result={result}
      anchor={markers[result.clinicLocationGuid]}
      setMarkerRef={setMarkerRef}
      isSelected={
        selectedClinic?.clinicLocationGuid === result.clinicLocationGuid ||
        selectedPractitioner?.clinicLocationGuid === result.clinicLocationGuid
      }
      handleMarkerClick={handleMarkerClick}
    />
  ));
};
